import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { orderApi, CreateOrderRequest } from '../api/orderApi';
import { BasketAPI } from '../api/basketApi';
import { identityApi } from '../api/identityApi';
import { useAuthEvent } from '../hooks/useAuthEvent';

interface CartItem {
  id: number;
  productId: string;
  count: number;
  pricePerUnit: number;
  name: string;
  brand: string;
  code: string;
}

interface UserInfo {
  firstName?: string;
  lastName?: string;
  email: string;
  phone: string;
  deliveryAddress?: string;
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isRefreshingToken, isLoading } = useAuth();
  const [formData, setFormData] = useState<Omit<CreateOrderRequest, 'anonymousId'>>({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    deliveryAddress: '',
    comment: ''
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [userDataLoaded, setUserDataLoaded] = useState(false);

  // Подписываемся на события авторизации
  useAuthEvent((user) => {
    console.log('User authenticated, refreshing checkout data...');
    setLoading(true);
    setUserDataLoaded(false);
    // Перезагружаем данные
    fetchData();
  });

  const fetchData = async () => {
    try {
      // Загружаем товары корзины
      const items = await BasketAPI.getItems();
      setCartItems(items);

      // Если пользователь авторизован, загружаем его данные
      if (isAuthenticated) {
        try {
          const userInfo = await identityApi.getUserInfo();
          setFormData(prev => ({
            ...prev,
            firstName: userInfo.firstName || '',
            lastName: userInfo.lastName || '',
            email: userInfo.email || '',
            phone: userInfo.phone || '',
            deliveryAddress: userInfo.deliveryAddress || ''
          }));
          setUserDataLoaded(true);
        } catch (userError) {
          console.error("Ошибка при загрузке данных пользователя:", userError);
          // Продолжаем без данных пользователя
        }
      }
    } catch (error) {
      console.error("Ошибка при загрузке корзины:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Ждем завершения инициализации и обновления токена перед загрузкой данных
    if (!isLoading && !isRefreshingToken) {
      fetchData();
    }
  }, [isAuthenticated, isRefreshingToken, isLoading]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await orderApi.createOrder({
        ...formData,
        anonymousId: '' // anonymousId будет установлен в API
      });
      
      if (response.success && response.data) {
        if (isAuthenticated) {
          navigate('/account/orders');
        } else {
          navigate('/order-success', { 
            state: { 
              orderId: response.data.id,
              message: 'Ваш заказ успешно оформлен! Мы свяжемся с вами в ближайшее время.' 
            } 
          });
        }
      } else {
        setError(response.message || 'Ошибка при оформлении заказа');
      }
    } catch (err) {
      setError('Произошла ошибка при оформлении заказа');
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalAmount = cartItems.reduce((sum, item) => {
    if (!item.pricePerUnit || !item.count) return sum;
    return sum + item.pricePerUnit * item.count;
  }, 0);

  if (loading) {
    return <div className="text-gray-300 mt-12">Загружаем информацию...</div>;
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:py-10">
      <h1 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">Оформление заказа</h1>
      
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
        {/* Левая колонка с товарами */}
        <div className="w-full lg:w-1/2 order-2 lg:order-1">
          <div className="bg-white rounded-lg shadow p-4 md:p-6">
            <h2 className="text-base md:text-lg font-medium mb-4 pt-0 mt-0">Состав заказа</h2>
            {cartItems.length === 0 ? (
              <div className="text-gray-500">Корзина пуста</div>
            ) : (
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex justify-between items-center border-b pb-4">
                    <div>
                      <div className="font-medium">{item.name}</div>
                      <div className="text-sm text-gray-500">{item.brand} - {item.code}</div>
                      <div className="text-sm text-gray-500">Количество: {item.count}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{item.pricePerUnit.toFixed(2)} ₽</div>
                      <div className="text-sm text-gray-500">{(item.pricePerUnit * item.count).toFixed(2)} ₽</div>
                    </div>
                  </div>
                ))}
                <div>
                  <div className="flex justify-between items-center font-medium">
                    <span>Итого:</span>
                    <span>{totalAmount.toFixed(2)} ₽</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Правая колонка с формой */}
        <div className="w-full lg:w-1/2 order-1 lg:order-2">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-2xl md:rounded-3xl mb-4 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 bg-white rounded-lg shadow p-4 md:p-6">
            <div>
              <label className="pl-2 md:pl-4 block text-sm font-medium mb-1">Имя</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-2xl md:rounded-3xl placeholder-gray-400 text-sm md:text-base"
                placeholder="Введите имя"
              />
            </div>

            <div>
              <label className="pl-2 md:pl-4 block text-sm font-medium mb-1">Фамилия</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-2xl md:rounded-3xl placeholder-gray-400 text-sm md:text-base"
                placeholder="Введите фамилию"
              />
            </div>

            <div>
              <label className="pl-2 md:pl-4 block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-2xl md:rounded-3xl placeholder-gray-400 text-sm md:text-base"
                placeholder="Введите email"
              />
            </div>

            <div>
              <label className="pl-2 md:pl-4 block text-sm font-medium mb-1">Телефон</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-2xl md:rounded-3xl placeholder-gray-400 text-sm md:text-base"
                placeholder="Введите телефон"
              />
            </div>

            <div>
              <label className="pl-2 md:pl-4 block text-sm font-medium mb-1">Адрес доставки</label>
              <input
                type="text"
                name="deliveryAddress"
                value={formData.deliveryAddress}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-2xl md:rounded-3xl placeholder-gray-400 text-sm md:text-base"
                placeholder="Введите адрес доставки"
              />
              <p className="pl-2 md:pl-4 text-xs text-gray-500/50 mt-2">
                Стоимость доставки будет расчитана при подтверждении заказа. В случае самовывоза оставьте поле пустым
              </p>
            </div>

            <div>
              <label className="pl-2 md:pl-4 block text-sm font-medium mb-1">Комментарий к заказу</label>
              <textarea
                name="comment"
                value={formData.comment}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-2xl md:rounded-3xl placeholder-gray-400 text-sm md:text-base"
                placeholder="Введите комментарий к заказу"
                rows={3}
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting || cartItems.length === 0}
              className="w-full bg-black text-white py-2.5 md:py-2 px-4 rounded-2xl md:rounded-3xl hover:bg-gray-800 transition-colors disabled:bg-gray-400 text-sm md:text-base"
            >
              {isSubmitting ? 'Оформление...' : 'Оформить заказ'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
} 